import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from "./index.module.css";
import Keyboard from '../../components/shared/Keyboard';
import Input from '../../components/shared/Input';
import { Button } from '@mui/material';
import fetchAllTeams from '../../components/shared/fetchAllTeams';
import StarRating from '../../components/shared/StarRating';

/** Player with teamId so we can call the rate API after the round */
type Player = {
  id: number;
  name: string;
  position: string;
  age: string;
  number: string;
  photo: string;
  rating?: number;
  teamId: number;
};

type Guess = {
  letter: string,
  status: 'unchecked' | 'correct' | 'outOfPlace' | 'incorrect'
};

type Team = {
  id: number;
  name: string;
  players: Omit<Player, 'teamId'>[];
};

type MainContentProps = {
  updatePlayerRating?: (playerId: number, rating: number, squadId: number) => Promise<unknown>;
  ratePlayer?: (playerId: number, rating: number) => Promise<unknown>;
};

const MainContent = ({ updatePlayerRating, ratePlayer }: MainContentProps) => {
  const [squad, setSquad] = useState<Player[] | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [guesses, setGuesses] = useState<Guess[][]>(new Array(5).fill([]));
  const [currentGuessIndex, setCurrentGuessIndex] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [contentWidth, setContentWidth] = useState('800px');
  /** All players (rated and unrated) with teamId for the rate API */
  const [playablePlayers, setPlayablePlayers] = useState<Player[]>([]);
  const [disabledKeys, setDisabledKeys] = useState<string[]>([]);
  const [blurValue, setBlurValue] = useState(30);
  const [postGameRating, setPostGameRating] = useState(0);
  const [hasRatedThisPlayer, setHasRatedThisPlayer] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleKeyPress = useCallback((key: string) => {
    if(gameOver || !selectedPlayer) return;
    if (key === '<-') {
      setGuesses(prevGuesses => {
        const newGuesses = [...prevGuesses];
        newGuesses[currentGuessIndex] = newGuesses[currentGuessIndex].slice(0, -1);
        return newGuesses;
      });
    } else if (guesses[currentGuessIndex].length < selectedPlayer.name.length) {
      setGuesses(prevGuesses => {
        const newGuesses = [...prevGuesses];
        newGuesses[currentGuessIndex] = [...newGuesses[currentGuessIndex], { letter: key, status: 'unchecked' as const }];
        return newGuesses;
      });
    }    
  }, [guesses, currentGuessIndex, selectedPlayer, setGuesses, gameOver]);

  const normalizePlayerName = useCallback((name: string) => {
    let n = name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (n.includes('. ')) {
      const nameParts = n.split('. ');
      n = nameParts[1];
    }
    return n;
  }, []);

  const startNextGame = useCallback(() => {
    if (playablePlayers.length > 0) {
      setBlurValue(30);
      setPostGameRating(0);
      setHasRatedThisPlayer(false);
      const newPlayer = { ...playablePlayers[Math.floor(Math.random() * playablePlayers.length)] };
      newPlayer.name = normalizePlayerName(newPlayer.name);
      setSelectedPlayer(newPlayer);
      setDisabledKeys([]);
      setGameOver(false);
      setGameWon(false);
      setGuesses(new Array(5).fill([]));
      setCurrentGuessIndex(0);
    } else {
      console.log("No players found");
    }
  }, [playablePlayers, normalizePlayerName]);

  useEffect(() => {
    setLoadError(null);
    fetchAllTeams()
      .then((data: Team[]) => {
        const teams = data || [];
        console.log('[Home] GET /teams response: %d team(s)', teams.length);
        teams.forEach((team) => {
          const count = Array.isArray(team.players) ? team.players.length : 0;
          console.log(`  - teamId ${team.id}: "${team.name}" => ${count} player(s)`);
        });
        const withTeamId: Player[] = teams.flatMap((team) =>
          (team.players || []).map((p) => ({ ...p, teamId: team.id }))
        );
        console.log('[Home] Total players for game:', withTeamId.length);
        setSquad(withTeamId);
        setPlayablePlayers(withTeamId);
      })
      .catch((err) => {
        console.error('Failed to load teams/players', err);
        setLoadError('Could not load players. Is the server running?');
        setSquad([]);
        setPlayablePlayers([]);
      });
  }, []);

  useEffect(() => {
    setGuesses(new Array(5).fill([]));
  }, [selectedPlayer]);

  useEffect(() => {
    if (playablePlayers.length > 0 && !selectedPlayer) {
      const randomPlayer = { ...playablePlayers[Math.floor(Math.random() * playablePlayers.length)] };
      randomPlayer.name = normalizePlayerName(randomPlayer.name);
      setSelectedPlayer(randomPlayer);
    }
  }, [playablePlayers, selectedPlayer, normalizePlayerName]);
  
  

  useEffect(() => {
    if (selectedPlayer) {
      setContentWidth(`${selectedPlayer.name.length * 55}px`);
    }
  }, [selectedPlayer]);

  useEffect(() => {
    const handleKeyDown = (event: { key: string; }) => {
      if(gameOver) return;
      const upperKey = event.key.toUpperCase();
      console.log(upperKey);
      
      if (upperKey === 'ENTER') {
        handleSubmit();
      } else if (upperKey === 'BACKSPACE') {
        handleKeyPress('<-');
      } else if (upperKey.length > 1){
        return;
      } else {
        handleKeyPress(upperKey);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
  
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleKeyPress]);

  const hasRateUI = Boolean(updatePlayerRating && ratePlayer);

  useEffect(() => {
    if (!gameOver) return;
    if (hasRateUI) {
      return;
    }
    const t = setTimeout(startNextGame, 8000);
    return () => clearTimeout(t);
  }, [gameOver, startNextGame, hasRateUI]);

  useEffect(() => {
    if (!gameOver || !hasRateUI || !hasRatedThisPlayer) return;
    const t = setTimeout(startNextGame, 2500);
    return () => clearTimeout(t);
  }, [gameOver, hasRateUI, hasRatedThisPlayer, startNextGame]);

  const handlePostGameRateSubmit = async () => {
    if (!selectedPlayer || postGameRating === 0 || !updatePlayerRating || !ratePlayer) return;
    try {
      await updatePlayerRating(selectedPlayer.id, postGameRating, selectedPlayer.teamId);
      await ratePlayer(selectedPlayer.id, postGameRating);
      setHasRatedThisPlayer(true);
    } catch (err) {
      console.error('Failed to submit rating', err);
    }
  };

  if (squad === null) {
    return <div className={styles.container}>Loading...</div>;
  }

  if (loadError) {
    return (
      <div className={styles.container}>
        <p className={styles.emptyMessage}>{loadError}</p>
      </div>
    );
  }

  if (playablePlayers.length === 0 || !selectedPlayer) {
    return (
      <div className={styles.container}>
        <p className={styles.emptyMessage}>
          No players available yet. Add squad data (e.g. run the populate script) or go to Rate Player to load teams.
        </p>
        <Button component={Link} to="/rate" variant="outlined" sx={{ mt: 2 }}>
          Go to Rate Player
        </Button>
      </div>
    );
  }

  let playerName = selectedPlayer.name;
  if(playerName.includes('.')){
      const nameParts = playerName.split('. ');
      playerName = nameParts[1];
  }

  const handleSubmit = () => {
    // Perform the letter-by-letter comparison
    if (guesses[currentGuessIndex]) {
      const currentGuess = guesses[currentGuessIndex];
      let playerName = selectedPlayer.name.toUpperCase();
      // Treat hyphens as spaces
      playerName = playerName.replace(/-/g, ' ');
  
      const playerNameNoSpaces = playerName.replace(/ /g, '');
      const guessNoSpaces = currentGuess.map(g => g.letter).join('').replace(/ /g, '');
  
      const updatedGuess = Array.from(playerName).map((char, index) => {
        if (char === ' ') {
          return null;
        } else {
          const adjustedIndex = Array.from(playerName.slice(0, index)).filter((char) => char !== ' ').length;
          const letter = guessNoSpaces[adjustedIndex];
          
          if (playerNameNoSpaces.includes(letter)) {
            if (playerNameNoSpaces[adjustedIndex] === letter) {
              // The letter is in the correct position
              return { letter, status: 'correct' as const };
            } else {
              // The letter exists but is in the wrong position
              return { letter, status: 'outOfPlace' as const };
            }
          } else {
            // The letter does not exist in the player's name
            return { letter, status: 'incorrect' as const };
          }
        }
      }).filter(guess => guess !== null) as Guess[];
  
      // Update the current guess with the comparison results
      setGuesses(prevGuesses => {
        const newGuesses = [...prevGuesses];
        newGuesses[currentGuessIndex] = updatedGuess;
        return newGuesses;
      });

      const incorrectKeys = updatedGuess.filter(guess => guess.status === 'incorrect').map(guess => guess.letter);


      setDisabledKeys(prevKeys => [...prevKeys, ...incorrectKeys]);

      console.log('Disabled keys: ', disabledKeys);  // Add this line to log the disabled keys.

      const allCorrect = updatedGuess.every(guess => guess.status === 'correct');
      const nextIndex = (currentGuessIndex < 4) ? currentGuessIndex + 1 : currentGuessIndex;
      const gameOverStatus = (allCorrect && currentGuessIndex <= 5) || (currentGuessIndex === 4 && !allCorrect);
      const gameWonStatus = allCorrect && currentGuessIndex <= 5;
  
      // Update states in a batch to avoid multiple renderings
      setCurrentGuessIndex(nextIndex);
      setGameOver(gameOverStatus);
      setGameWon(gameWonStatus);
      setBlurValue(blurValue - 5);
  
      // Reset the 'correct' status of each guess when moving to the next guess
      if (currentGuessIndex < 4 && allCorrect) {
        setGuesses(prevGuesses => {
          const newGuesses = [...prevGuesses];
          newGuesses[nextIndex] = newGuesses[nextIndex].map(guess => ({ ...guess, status: 'unchecked' }));
          return newGuesses;
        });
        setBlurValue(0);
      } else if (currentGuessIndex === 4){
        setBlurValue(0);
      }
    }
  };  

  return (
    <div className={styles.mainContent} style={{ maxWidth: contentWidth }}>
      <div className={styles.container}>
        <div>
          {/* <h2 style={{color: "black"}}>{selectedPlayer.name} {selectedPlayer.name.length}</h2> */}
          <img src={selectedPlayer.photo} alt={selectedPlayer.name} style={{filter: `blur(${blurValue}px)`}}></img>
          <p >Age: {selectedPlayer.age}</p>
          <p >Number: {selectedPlayer.number}</p>
          <p >Position: {selectedPlayer.position}</p>
        </div>
        {guesses.map((guess, index) => (
          <Input 
            inputValue={guess.map(g => g.letter).join('')} 
            playerName={selectedPlayer.name} 
            disabled={index !== currentGuessIndex} 
            guesses={guess}
            gameOver={gameOver}
            key={index}
          />
        ))}
        {gameOver ? (gameWon ?(
          <Button disabled style={{ cursor: 'default' }}>You Won 🥇</Button>
        ) : <Button disabled style={{ cursor: 'default' }}>You Lost 😭</Button>) : (
          <Button variant='outlined' onClick={handleSubmit}>Submit</Button>
        )}
        {gameOver && selectedPlayer && (updatePlayerRating && ratePlayer) && (
          <div className={styles.rateBlock}>
            <p className={styles.rateLabel}>
              {hasRatedThisPlayer ? 'Thanks for rating!' : 'Rate this player'}
            </p>
            {!hasRatedThisPlayer ? (
              <>
                <StarRating initialRating={postGameRating} onRatingChange={setPostGameRating} />
                <Button
                  variant="outlined"
                  onClick={handlePostGameRateSubmit}
                  disabled={postGameRating === 0}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  Submit rating
                </Button>
              </>
            ) : (
              <p className={styles.rateLabel}>Next round in 2s…</p>
            )}
            <Button
              variant="outlined"
              onClick={startNextGame}
              size="small"
              sx={{ mt: 1.5 }}
            >
              Next player
            </Button>
          </div>
        )}
        <Keyboard onKeyPress={handleKeyPress} disabledKeys={disabledKeys}/>
      </div>
    </div>
  );
}

export default MainContent;
